import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import PlatformClubsList from "./PlatformClubsList";
import PlatformPageHeader from "./PlatformPageHeader";
import {
  downloadOutreachCsv,
  filterClinics,
  filterOutreachSegment,
  formatRub,
  getClinicPhone,
  summarizeClinics,
} from "./platformClubsUtils";
import s from "./platformAdmin.module.css";

const FILTERS = [
  { id: "all", label: "Все" },
  { id: "outreach", label: "Для прозвона" },
  { id: "active", label: "Активные" },
  { id: "trial", label: "Пробные" },
  { id: "expiring", label: "Истекают" },
  { id: "inactive", label: "Без активности" },
  { id: "expired", label: "Истекли" },
];

const EXPORT_SEGMENTS = [
  {
    id: "outreach",
    label: "Весь список для прозвона",
    hint: "Демо без оплаты, истекшие, без активности, истекающие",
  },
  {
    id: "with_phone",
    label: "Только с телефоном",
    hint: "Те же причины, но есть номер для звонка",
  },
  {
    id: "demo_unpaid",
    label: "Демо не оплатили",
    hint: "Клубы на пробном периоде без оплаты",
  },
  {
    id: "demo_expired",
    label: "Демо истекло",
    hint: "Пробный период закончился",
  },
  {
    id: "inactive",
    label: "Без активности",
    hint: "Нет входов/занятий более 7 дней или никогда",
  },
  {
    id: "expiring",
    label: "Подписка истекает",
    hint: "Осталось ≤ 14 дней",
  },
  {
    id: "overdue",
    label: "Оплата просрочена",
    hint: "Срок оплаты уже прошёл",
  },
  {
    id: "current",
    label: "Текущий фильтр на экране",
    hint: "То, что сейчас видно в списке",
  },
];

function applyStatusFilter(clinics, filterId) {
  const list = clinics || [];
  if (filterId === "all") return list;
  if (filterId === "outreach") return filterOutreachSegment(list, "outreach");
  if (filterId === "active") {
    return list.filter(
      (c) => c.billing?.is_paid && !c.billing?.is_trial && c.status !== "suspended",
    );
  }
  if (filterId === "trial") {
    return list.filter((c) => c.billing?.is_trial && !c.billing?.is_demo_expired);
  }
  if (filterId === "expiring") {
    return list.filter(
      (c) =>
        c.billing?.days_left != null &&
        c.billing.days_left >= 0 &&
        c.billing.days_left <= 14,
    );
  }
  if (filterId === "inactive") {
    return filterOutreachSegment(list, "inactive");
  }
  if (filterId === "expired") {
    return list.filter(
      (c) =>
        c.billing?.is_demo_expired ||
        (c.billing?.days_left != null && c.billing.days_left < 0),
    );
  }
  return list;
}

export default function PlatformTenants() {
  const location = useLocation();
  const [clinics, setClinics] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState(location.state?.msg || "");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    setLoading(true);
    platformAPI
      .getClinics()
      .then((r) => setClinics(r.clinics || []))
      .catch((e) =>
        setErr(e.response?.data?.error || "Ошибка загрузки клубов"),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const summary = useMemo(() => summarizeClinics(clinics), [clinics]);

  const filtered = useMemo(() => {
    const byQuery = filterClinics(clinics, query);
    return applyStatusFilter(byQuery, filter);
  }, [clinics, query, filter]);

  const setStatus = async (id, status) => {
    try {
      await platformAPI.updateClinicStatus(id, status);
      setMsg("Статус обновлён");
      load();
    } catch (e) {
      setErr(e.response?.data?.error || "Ошибка");
    }
  };

  const deleteClinic = async (id) => {
    if (deletingId) return;
    if (
      !window.confirm(
        "Удалить клуб? Клуб исчезнет из списка, доступ пользователей будет заблокирован, slug освободится. Данные останутся для статистики.",
      )
    ) {
      return;
    }
    setErr("");
    setMsg("");
    setDeletingId(id);
    try {
      await platformAPI.deleteClinic(id);
      setClinics((prev) => prev.filter((c) => c.id !== id));
      setMsg("Клуб удалён из списка");
    } catch (e) {
      setErr(e.response?.data?.error || "Не удалось отключить клуб");
      load();
    } finally {
      setDeletingId(null);
    }
  };

  const exportSegment = (segmentId) => {
    const source =
      segmentId === "current"
        ? filtered
        : filterOutreachSegment(filterClinics(clinics, query), segmentId);

    if (!source.length) {
      setErr("Нет клубов для выгрузки по этому сегменту");
      setMsg("");
      return;
    }

    const withoutPhone = source.filter((c) => !getClinicPhone(c)).length;
    const count = downloadOutreachCsv(
      source,
      segmentId === "current"
        ? `baysport-kluby-${filter}`
        : `baysport-prozvony-${segmentId}`,
    );
    setErr("");
    setMsg(
      withoutPhone > 0
        ? `Выгружено ${count} клубов (без телефона: ${withoutPhone}). CSV готов для менеджера.`
        : `Выгружено ${count} клубов. CSV готов для менеджера.`,
    );
    setExportOpen(false);
  };

  return (
    <div className={s.page}>
      <PlatformPageHeader
        title="Клубы"
        lead="Карточки клубов, список на прозвон и выгрузка CSV для менеджера."
        actions={
          <button
            type="button"
            className={s.heroActionBtn}
            onClick={() => setExportOpen((v) => !v)}
          >
            Выгрузить для прозвонов
          </button>
        }
      />

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      {exportOpen ? (
        <section className={s.exportPanel} aria-label="Выгрузка для прозвонов">
          <div className={s.exportPanelHead}>
            <div>
              <h2 className={s.exportPanelTitle}>Выгрузка для менеджера</h2>
              <p className={s.exportPanelLead}>
                CSV с телефоном, email, статусом и причиной звонка — открывается в
                Excel.
              </p>
            </div>
            <button
              type="button"
              className={s.heroActionBtnGhost}
              onClick={() => setExportOpen(false)}
            >
              Закрыть
            </button>
          </div>
          <div className={s.exportSegmentGrid}>
            {EXPORT_SEGMENTS.map((seg) => {
              const count =
                seg.id === "current"
                  ? filtered.length
                  : filterOutreachSegment(clinics, seg.id).length;
              return (
                <button
                  key={seg.id}
                  type="button"
                  className={s.exportSegmentCard}
                  onClick={() => exportSegment(seg.id)}
                  disabled={count === 0}
                >
                  <strong>{seg.label}</strong>
                  <span>{seg.hint}</span>
                  <em>{count} клубов</em>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className={s.clubsSummaryGrid}>
        <article className={s.clubsSummaryCard}>
          <span>Всего</span>
          <strong>{summary.total}</strong>
        </article>
        <article className={s.clubsSummaryCard}>
          <span>Активные</span>
          <strong>{summary.active}</strong>
        </article>
        <article className={s.clubsSummaryCard}>
          <span>Пробные</span>
          <strong>{summary.trial}</strong>
        </article>
        <article
          className={`${s.clubsSummaryCard} ${s.clubsSummaryWarn}`}
          role="button"
          tabIndex={0}
          onClick={() => setFilter("outreach")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setFilter("outreach");
          }}
          title="Показать список для прозвона"
        >
          <span>Для прозвона</span>
          <strong>{summary.outreach}</strong>
        </article>
        <article className={s.clubsSummaryCard}>
          <span>С телефоном</span>
          <strong>{summary.outreachWithPhone}</strong>
        </article>
        <article className={s.clubsSummaryCard}>
          <span>Без активности 7д</span>
          <strong>{summary.inactive7d}</strong>
        </article>
        <article className={`${s.clubsSummaryCard} ${s.clubsSummaryWarn}`}>
          <span>Истекают 14д</span>
          <strong>{summary.expiring14}</strong>
        </article>
        <article className={s.clubsSummaryCard}>
          <span>Спортсмены</span>
          <strong>{formatRub(summary.clients)}</strong>
        </article>
      </section>

      <div className={s.clubsPageBody}>
        <div className={s.clubsToolbar}>
          <div className={s.clubsToolbarRow}>
            <input
              type="search"
              className={s.clubSearch}
              placeholder="Поиск клуба или владельца…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {filter === "outreach" || filtered.length ? (
              <button
                type="button"
                className={s.clubsExportInline}
                onClick={() => exportSegment("current")}
                disabled={!filtered.length}
              >
                CSV текущего списка ({filtered.length})
              </button>
            ) : null}
          </div>
          <div className={s.clubsFilterChips} role="tablist" aria-label="Фильтр клубов">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`${s.clubsFilterChip} ${filter === f.id ? s.clubsFilterChipActive : ""}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                {f.id === "outreach" ? ` · ${summary.outreach}` : ""}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className={s.loading}>Загрузка клубов…</div>
        ) : (
          <PlatformClubsList
            clinics={filtered}
            showStatusSelect
            onStatusChange={setStatus}
            showDeleteClinicActions
            onDeleteClinic={deleteClinic}
            deletingId={deletingId}
            showOutreachReasons={
              filter === "outreach" ||
              filter === "inactive" ||
              filter === "expired" ||
              filter === "expiring" ||
              filter === "trial"
            }
            emptyText={
              query || filter !== "all"
                ? "Нет клубов по текущему фильтру"
                : "Клубов пока нет"
            }
          />
        )}
      </div>
    </div>
  );
}
